/* eslint-disable react/no-array-index-key */
// QrveyPanels.tsx
import React, { useEffect } from 'react';
import { css } from '@emotion/react';
import { ChartType, QrveyPanel } from 'redux/schemas/app/analytics';

import { extractTextFromHTML } from 'shared/utils';
import NvTruncatedTextWithTooltip from 'shared/components/nv-truncated-text-with-tooltip';

import { halfSpacing } from 'styles/global_defaults/scaffolding';
import { baseStyles, questionSetStyles } from './custom-styles';

type PanelProps = {
  panels: QrveyPanel[];
};

const styles = css`
  &.panel-container {
    display: flex;
    flex-wrap: wrap;
    gap: ${halfSpacing}px;
    box-sizing: border-box;
    width: 100%;
    align-items: stretch;
  }

  .box {
    flex: 0 0 calc((100% - ${halfSpacing}px) / 2);
    max-width: calc((100% - ${halfSpacing}px) / 2);
    min-width: 0;
    box-sizing: border-box;
    .an-panel {
      height: 400px;
    }
  }

  /* Fixed-size small stat panels */
  .number-panel {
    flex: 0 0 200px !important;
    max-width: 200px !important;
    width: 200px !important;
    min-width: 0;
    height: 200px;
  }

  /* If a number-panel is immediately followed by a box:
     make that next box fill the remaining row width */
  .number-panel + .box {
    flex: 0 0 calc(100% - 200px - ${halfSpacing}px) !important;
    max-width: calc(100% - 200px - ${halfSpacing}px) !important;
  }

  /* Full-width panels */
  .box:last-child:not(.half-panel), .full-panel {
    flex: 0 0 100% !important;
    max-width: 100% !important;
  }
  .an-panel {
    box-shadow: none !important;
  }
`;

const Panel = (props: { panelIndex: number }) => {
  useEffect(() => {
    // We cannot directly inject custom styles to shadow root DOM
    const checkForElement = () => {
      const panelElement = document.querySelector(`.an-panel-${props.panelIndex}`);

      if (panelElement?.shadowRoot) {
        const { shadowRoot } = panelElement;

        if (!shadowRoot.querySelector('style[data-injected]')) {
          const style = document.createElement('style');
          style.textContent = `
          ${baseStyles}
          ${questionSetStyles}
          `;
          style.setAttribute('data-injected', 'true');
          shadowRoot.appendChild(style);
        }
        return true;
      }
      return false;
    };

    // check every 500ms until the element is found
    const intervalId = setInterval(() => {
      if (checkForElement()) {
        clearInterval(intervalId);
      }
    }, 500);

    return () => clearInterval(intervalId);
  }, [props.panelIndex]);

  return (
    <an-panel
      config={`anPanelConfig${props.panelIndex}`}
      class={`an-panel-${props.panelIndex} mt-2`}
    />
  );
};

const QrveyPanels = ({ panels }: PanelProps) => (
  <div className='panel-container d-flex mt-6 w-100' css={styles}>
    {panels?.map((panel, index) => (
      <React.Fragment key={`${panel.chartId}_${panel.chartType}_${index}`}>
        {panel.customTitle && (
          <>
            {panel.chartType === ChartType.ALL ? (
              <div
                className='course-title-small ml-1 w-100'
                aria-label={panel.customTitle}
              >
                {panel.customTitle}
              </div>
            ) : (
              panel.customTitle && (
              <NvTruncatedTextWithTooltip
                text={extractTextFromHTML(panel.customTitle)}
                linesCount={2}
                className='course-title-small ml-1'
              />
              )
            )}
          </>
        )}
        <div
          className={`d-flex flex-column justify-content-center box mb-5 ${panel?.className}`}
          key={index}
        >
          {panel?.title && (
            <NvTruncatedTextWithTooltip
              text={extractTextFromHTML(panel.title)}
              linesCount={2}
              /* For full-panel, allow height to adjust based on content. */
              fixedHeight={panel.className !== 'full-panel'}
              className='course-title-small ml-1'
            />
          )}
          {panel?.titleInfo && (
            <div
              className='course-title-small ml-1 mt-2'
              aria-label={panel.titleInfo}
            >
              {panel.titleInfo}
            </div>
          )}
          <Panel panelIndex={index} />
        </div>
      </React.Fragment>
    ))}
  </div>
);

export default QrveyPanels;
